import React, { useCallback, useMemo, useState } from 'react'

import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Block as BlockIcon, CheckCircleOutline } from '@material-ui/icons'
import CloseIcon from '@material-ui/icons/Close'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import SearchIcon from '@material-ui/icons/Search'
import { KeyboardDateTimePicker } from '@material-ui/pickers'
import { toast } from 'react-toastify'

import { Container } from '../../components'
import { Selector } from '../../components/Selector/Selector'
import {
    BulkCreateRenewalOffersInput,
    Concession,
} from '../../models/Concession'
import { useConcessions } from '../../hooks/useConcessions'
import {
    useBlockedLeases,
    useEligibleLeases,
    useOccupancyUnits,
    useRenewalOffers,
} from '../../hooks/useRenewalOffers'
import { displayApiDate } from '../Concessions/utils'
import { useConcessionStyles } from '../Concessions/styles'

interface Props {
    open: boolean
    handleClose: () => void
    leasingSeasonId?: number
}

export const CreateOffersModal: React.FC<Props> = ({
    open,
    handleClose,
    leasingSeasonId,
}) => {
    const theme = useTheme()
    const classes = useConcessionStyles()

    const { concessions } = useConcessions()
    const { eligibleLeases, isLoading: leasesLoading } = useEligibleLeases(open)
    const {
        blockedLeases,
        isLoading: blockedLoading,
        unblockLease,
    } = useBlockedLeases(open)
    const { units } = useOccupancyUnits(open)
    const { bulkCreate, blockLease } = useRenewalOffers()

    const [proposedRent, setProposedRent] = useState('')
    const [proposedTermMonths, setProposedTermMonths] = useState('12')
    const [concessionIds, setConcessionIds] = useState<number[]>([])
    const [expiresAt, setExpiresAt] = useState<Date | null>(null)
    const [unitConfigId, setUnitConfigId] = useState<number | null>(null)
    const [selectedLeases, setSelectedLeases] = useState<Set<number>>(new Set())
    const [searchText, setSearchText] = useState('')
    const [showBlocked, setShowBlocked] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [blockConfirm, setBlockConfirm] = useState<{
        leaseId: number
        tenantName: string
    } | null>(null)
    const [unblockConfirm, setUnblockConfirm] = useState<{
        leaseId: number
        tenantName: string
    } | null>(null)

    const closeBlockConfirm = useCallback(() => setBlockConfirm(null), [])
    const closeUnblockConfirm = useCallback(() => setUnblockConfirm(null), [])

    const unitConfigOptions = useMemo(() => {
        const seen = new Map<number, string>()
        for (const unit of units) {
            if (!seen.has(unit.unit_config)) {
                seen.set(unit.unit_config, unit.unit_config_name ?? '')
            }
        }
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [units])

    const unitIdsForSelectedConfig = useMemo(() => {
        if (unitConfigId === null) return null
        return new Set(
            units
                .filter((u) => u.unit_config === unitConfigId)
                .map((u) => u.id),
        )
    }, [units, unitConfigId])

    const filteredLeases = useMemo(() => {
        let base = eligibleLeases
        if (unitIdsForSelectedConfig) {
            base = base.filter((l) => unitIdsForSelectedConfig.has(l.unit))
        }
        if (!searchText.trim()) return base
        const q = searchText.toLowerCase()
        return base.filter(
            (l) =>
                l.tenant_name.toLowerCase().includes(q) ||
                l.tenant_email.toLowerCase().includes(q) ||
                l.unit_name.toLowerCase().includes(q) ||
                l.area_config_name.toLowerCase().includes(q),
        )
    }, [eligibleLeases, searchText, unitIdsForSelectedConfig])

    const handleUnitConfigChange = (id: number) => {
        const nextId = id === -1 ? null : id
        setUnitConfigId(nextId)
        if (nextId === null) return
        const allowedUnits = new Set(
            units.filter((u) => u.unit_config === nextId).map((u) => u.id),
        )
        setSelectedLeases((prev) => {
            const next = new Set<number>()
            for (const leaseId of Array.from(prev)) {
                const lease = eligibleLeases.find((l) => l.id === leaseId)
                if (lease && allowedUnits.has(lease.unit)) {
                    next.add(leaseId)
                }
            }
            return next
        })
    }

    const activeConcessions = concessions.filter((c: Concession) => c.is_active)

    const unitConfigSelectorData = [
        { id: -1, name: 'All floor plans' } as { id: number; name: string },
        ...unitConfigOptions,
    ]

    const toggleLease = (id: number) => {
        setSelectedLeases((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
        setFieldErrors((prev) => {
            const next = { ...prev }
            delete next.tenants
            return next
        })
    }

    const toggleAll = () => {
        if (selectedLeases.size === filteredLeases.length) {
            setSelectedLeases(new Set())
        } else {
            setSelectedLeases(new Set(filteredLeases.map((l) => l.id)))
        }
        setFieldErrors((prev) => {
            const next = { ...prev }
            delete next.tenants
            return next
        })
    }

    const rentAmount = parseFloat(proposedRent.trim())
    const hasValidRent = !Number.isNaN(rentAmount) && rentAmount > 0

    const validate = (): Record<string, string> => {
        const errors: Record<string, string> = {}
        if (!hasValidRent) {
            errors.rent = 'Monthly rent is required and must be greater than 0.'
        }
        const term = parseInt(proposedTermMonths, 10)
        if (
            !proposedTermMonths ||
            Number.isNaN(term) ||
            !Number.isInteger(term) ||
            term < 1
        ) {
            errors.term = 'Term must be at least 1 month.'
        }
        if (selectedLeases.size === 0) {
            errors.tenants = 'Select at least one tenant.'
        }
        return errors
    }

    const handleCreate = () => {
        const errors = validate()
        setFieldErrors(errors)
        if (Object.keys(errors).length > 0) {
            toast.error(
                'Please fix the highlighted fields before creating offers.',
            )
            return
        }

        const payload: BulkCreateRenewalOffersInput = {
            lease_ids: Array.from(selectedLeases),
            proposed_term_months: parseInt(proposedTermMonths, 10),
            proposed_rent: rentAmount.toFixed(2),
            concessions: concessionIds.length > 0 ? concessionIds : undefined,
            expires_at: expiresAt ? expiresAt.toISOString() : null,
            ...(leasingSeasonId ? { leasing_season: leasingSeasonId } : {}),
        }

        bulkCreate.mutate(payload, {
            onSuccess: () => {
                setSelectedLeases(new Set())
                handleClose()
            },
        })
    }

    const handleBlock = (leaseId: number, tenantName: string) => {
        setBlockConfirm({ leaseId, tenantName })
    }

    const confirmBlock = () => {
        if (!blockConfirm) return
        blockLease.mutate(blockConfirm.leaseId)
        setSelectedLeases((prev) => {
            const next = new Set(prev)
            next.delete(blockConfirm.leaseId)
            return next
        })
        closeBlockConfirm()
    }

    const handleUnblock = (leaseId: number, tenantName: string) => {
        setUnblockConfirm({ leaseId, tenantName })
    }

    const confirmUnblock = () => {
        if (!unblockConfirm) return
        unblockLease.mutate(unblockConfirm.leaseId)
        closeUnblockConfirm()
    }

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper className={classes.paper}>
                        <Container
                            direction="column"
                            style={{
                                width: 'min(1280px, calc(100vw - 80px))',
                                height: 'calc(100vh - 120px)',
                                maxHeight: 820,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Header */}
                            <div className={classes.header}>
                                <div className={classes.headerIcon}>
                                    <AutorenewIcon />
                                </div>
                                <Container direction="column" flex={1}>
                                    <span className={classes.headerTitle}>
                                        Create Renewal Offers
                                    </span>
                                    <span className={classes.headerSubtitle}>
                                        Set default terms and select eligible
                                        tenants
                                    </span>
                                </Container>
                                <IconButton size="small" onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            {/* Body */}
                            <div className={classes.body}>
                                {/* Default terms */}
                                <div className={classes.section}>
                                    <div className={classes.sectionHeader}>
                                        <span className={classes.sectionTitle}>
                                            Default terms
                                        </span>
                                        <span className={classes.sectionHint}>
                                            Term and monthly rent apply to every
                                            draft.
                                        </span>
                                    </div>
                                    <div className={classes.row}>
                                        <TextField
                                            label="Monthly rent ($)"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={proposedRent}
                                            onChange={(e) => {
                                                setProposedRent(e.target.value)
                                                setFieldErrors((prev) => {
                                                    const next = { ...prev }
                                                    delete next.rent
                                                    return next
                                                })
                                            }}
                                            inputProps={{
                                                min: 0,
                                                step: '0.01',
                                            }}
                                            required
                                            error={!!fieldErrors.rent}
                                            helperText={
                                                fieldErrors.rent ||
                                                'Required. Used as the monthly rent on every selected draft.'
                                            }
                                            style={{ flex: 1 }}
                                        />
                                        <TextField
                                            label="Term (months)"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            value={proposedTermMonths}
                                            onChange={(e) => {
                                                setProposedTermMonths(
                                                    e.target.value,
                                                )
                                                setFieldErrors((prev) => {
                                                    const next = { ...prev }
                                                    delete next.term
                                                    return next
                                                })
                                            }}
                                            inputProps={{ min: 1, step: 1 }}
                                            required
                                            error={!!fieldErrors.term}
                                            helperText={fieldErrors.term}
                                            style={{ flex: 1 }}
                                        />
                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            style={{ flex: 1, minWidth: 180 }}
                                        >
                                            <InputLabel
                                                id="concession-multi-label"
                                                shrink={
                                                    concessionIds.length > 0
                                                }
                                            >
                                                Concessions
                                            </InputLabel>
                                            <Select
                                                labelId="concession-multi-label"
                                                multiple
                                                value={concessionIds}
                                                onChange={(e) =>
                                                    setConcessionIds(
                                                        e.target
                                                            .value as number[],
                                                    )
                                                }
                                                label={
                                                    concessionIds.length > 0
                                                        ? 'Concessions'
                                                        : undefined
                                                }
                                                renderValue={(selected) => {
                                                    const ids = selected as number[]
                                                    if (ids.length === 0)
                                                        return <></>
                                                    return (
                                                        <Box
                                                            display="flex"
                                                            flexWrap="nowrap"
                                                            alignItems="center"
                                                            style={{
                                                                gap: 4,
                                                                overflow:
                                                                    'hidden',
                                                            }}
                                                        >
                                                            {ids.map((id) => {
                                                                const c = activeConcessions.find(
                                                                    (x) =>
                                                                        x.id ===
                                                                        id,
                                                                )
                                                                return (
                                                                    <Chip
                                                                        key={id}
                                                                        label={
                                                                            c?.name ??
                                                                            id
                                                                        }
                                                                        size="small"
                                                                        style={{
                                                                            height: 20,
                                                                            fontSize:
                                                                                '0.675rem',
                                                                            fontWeight: 600,
                                                                            backgroundColor:
                                                                                '#d1fae5',
                                                                            color:
                                                                                '#047857',
                                                                            borderRadius: 6,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                )
                                                            })}
                                                        </Box>
                                                    )
                                                }}
                                                style={{ maxHeight: 40 }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                            marginTop: 4,
                                                        },
                                                    },
                                                    anchorOrigin: {
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    },
                                                    transformOrigin: {
                                                        vertical: 'top',
                                                        horizontal: 'left',
                                                    },
                                                    getContentAnchorEl: null,
                                                }}
                                            >
                                                {activeConcessions.map((c) => (
                                                    <MenuItem
                                                        key={c.id}
                                                        value={c.id}
                                                        style={{
                                                            paddingTop: 6,
                                                            paddingBottom: 6,
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={concessionIds.includes(
                                                                c.id,
                                                            )}
                                                            size="small"
                                                            style={{
                                                                color:
                                                                    '#047857',
                                                                padding:
                                                                    '0 8px 0 0',
                                                            }}
                                                        />
                                                        <ListItemText
                                                            primary={c.name}
                                                            primaryTypographyProps={{
                                                                variant:
                                                                    'body2',
                                                                style: {
                                                                    fontWeight: 500,
                                                                },
                                                            }}
                                                        />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <KeyboardDateTimePicker
                                            label="Expires at (optional)"
                                            format="MM/dd/yyyy hh:mm a"
                                            inputVariant="outlined"
                                            size="small"
                                            value={expiresAt}
                                            onChange={(date) =>
                                                setExpiresAt(date)
                                            }
                                            clearable
                                            style={{ flex: 1, minWidth: 180 }}
                                        />
                                    </div>
                                </div>

                                {/* Eligible leases picker */}
                                <div className={classes.section}>
                                    <div className={classes.sectionHeader}>
                                        <span className={classes.sectionTitle}>
                                            Eligible tenants
                                        </span>
                                        <span
                                            className={classes.sectionHint}
                                            style={
                                                fieldErrors.tenants
                                                    ? {
                                                          color: '#E2563B',
                                                          fontWeight: 600,
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {fieldErrors.tenants
                                                ? fieldErrors.tenants
                                                : selectedLeases.size > 0
                                                ? `${selectedLeases.size} selected`
                                                : `${filteredLeases.length} available`}
                                        </span>
                                    </div>

                                    <div
                                        className={classes.row}
                                        style={{
                                            marginBottom: theme.spacing(1.5),
                                        }}
                                    >
                                        <Selector
                                            label="Floor plan"
                                            size="small"
                                            currentValue={unitConfigId ?? -1}
                                            data={unitConfigSelectorData}
                                            getDisplayString={(o) => o.name}
                                            onChange={(e) => {
                                                handleUnitConfigChange(
                                                    e.target.value as number,
                                                )
                                            }}
                                            customStyle={{
                                                formControl: {
                                                    flex: 1,
                                                    minWidth: 180,
                                                },
                                            }}
                                        />
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            placeholder="Search by name, email, or unit..."
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(e.target.value)
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon
                                                            fontSize="small"
                                                            color="action"
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            style={{ flex: 2, minWidth: 220 }}
                                        />
                                    </div>

                                    {leasesLoading ? (
                                        <Container
                                            justifyContent="center"
                                            style={{
                                                padding: theme.spacing(4),
                                            }}
                                        >
                                            <CircularProgress size={28} />
                                        </Container>
                                    ) : eligibleLeases.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            style={{
                                                textAlign: 'center',
                                                padding: theme.spacing(3),
                                            }}
                                        >
                                            No eligible leases found. All
                                            tenants may already have active
                                            offers or renewals.
                                        </Typography>
                                    ) : filteredLeases.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            style={{
                                                textAlign: 'center',
                                                padding: theme.spacing(3),
                                            }}
                                        >
                                            No eligible tenants match this floor
                                            plan filter.
                                        </Typography>
                                    ) : (
                                        <TableContainer
                                            style={{ maxHeight: 360 }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={
                                                                    filteredLeases.length >
                                                                        0 &&
                                                                    selectedLeases.size ===
                                                                        filteredLeases.length
                                                                }
                                                                indeterminate={
                                                                    selectedLeases.size >
                                                                        0 &&
                                                                    selectedLeases.size <
                                                                        filteredLeases.length
                                                                }
                                                                onChange={
                                                                    toggleAll
                                                                }
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            Tenant
                                                        </TableCell>
                                                        <TableCell>
                                                            Unit
                                                        </TableCell>
                                                        <TableCell>
                                                            Lease dates
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            Block
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredLeases.map(
                                                        (lease) => (
                                                            <TableRow
                                                                key={lease.id}
                                                                hover
                                                            >
                                                                <TableCell padding="checkbox">
                                                                    <Checkbox
                                                                        checked={selectedLeases.has(
                                                                            lease.id,
                                                                        )}
                                                                        onChange={() =>
                                                                            toggleLease(
                                                                                lease.id,
                                                                            )
                                                                        }
                                                                        size="small"
                                                                        color="primary"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        style={{
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {
                                                                            lease.tenant_name
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                    >
                                                                        {
                                                                            lease.tenant_email
                                                                        }
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {
                                                                        lease.unit_name
                                                                    }{' '}
                                                                    ·{' '}
                                                                    {
                                                                        lease.area_config_name
                                                                    }{' '}
                                                                    {
                                                                        lease.area_label
                                                                    }
                                                                </TableCell>
                                                                <TableCell>
                                                                    {displayApiDate(
                                                                        lease.start_date,
                                                                    )}{' '}
                                                                    –{' '}
                                                                    {displayApiDate(
                                                                        lease.end_date,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Tooltip title="Block lease from renewals">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleBlock(
                                                                                    lease.id,
                                                                                    lease.tenant_name,
                                                                                )
                                                                            }
                                                                        >
                                                                            <BlockIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        ),
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </div>

                                {/* Blocked tenants */}
                                {blockedLeases.length > 0 && (
                                    <div className={classes.section}>
                                        <div
                                            className={classes.sectionHeader}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                setShowBlocked((v) => !v)
                                            }
                                        >
                                            <span
                                                className={classes.sectionTitle}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                }}
                                            >
                                                Blocked tenants
                                                <Chip
                                                    label={blockedLeases.length}
                                                    size="small"
                                                    style={{
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        backgroundColor:
                                                            '#E2563B15',
                                                        color: '#E2563B',
                                                    }}
                                                />
                                            </span>
                                            <span
                                                className={classes.sectionHint}
                                            >
                                                {showBlocked
                                                    ? 'Click to hide'
                                                    : 'Click to show'}
                                            </span>
                                        </div>
                                        <Collapse in={showBlocked}>
                                            {blockedLoading ? (
                                                <Container
                                                    justifyContent="center"
                                                    style={{
                                                        padding: theme.spacing(
                                                            3,
                                                        ),
                                                    }}
                                                >
                                                    <CircularProgress
                                                        size={24}
                                                    />
                                                </Container>
                                            ) : (
                                                <TableContainer
                                                    style={{ maxHeight: 240 }}
                                                >
                                                    <Table
                                                        size="small"
                                                        stickyHeader
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>
                                                                    Tenant
                                                                </TableCell>
                                                                <TableCell>
                                                                    Unit
                                                                </TableCell>
                                                                <TableCell>
                                                                    Lease dates
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    Unblock
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {blockedLeases.map(
                                                                (lease) => (
                                                                    <TableRow
                                                                        key={
                                                                            lease.id
                                                                        }
                                                                        hover
                                                                    >
                                                                        <TableCell>
                                                                            <Typography
                                                                                variant="body2"
                                                                                style={{
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    lease.tenant_name
                                                                                }
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="textSecondary"
                                                                            >
                                                                                {
                                                                                    lease.tenant_email
                                                                                }
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                lease.unit_name
                                                                            }{' '}
                                                                            ·{' '}
                                                                            {
                                                                                lease.area_config_name
                                                                            }{' '}
                                                                            {
                                                                                lease.area_label
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {displayApiDate(
                                                                                lease.start_date,
                                                                            )}{' '}
                                                                            –{' '}
                                                                            {displayApiDate(
                                                                                lease.end_date,
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <Tooltip title="Unblock tenant">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                        handleUnblock(
                                                                                            lease.id,
                                                                                            lease.tenant_name,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <CheckCircleOutline
                                                                                        fontSize="small"
                                                                                        style={{
                                                                                            color:
                                                                                                '#047857',
                                                                                        }}
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ),
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </Collapse>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className={classes.footer}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    color="secondary"
                                    onClick={handleClose}
                                    disabled={bulkCreate.isLoading}
                                    className={classes.cancelButton}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={handleCreate}
                                    disabled={
                                        bulkCreate.isLoading ||
                                        selectedLeases.size === 0 ||
                                        !hasValidRent
                                    }
                                    className={classes.saveButton}
                                >
                                    {bulkCreate.isLoading
                                        ? 'Creating...'
                                        : `Create ${
                                              selectedLeases.size
                                          } draft offer${
                                              selectedLeases.size === 1
                                                  ? ''
                                                  : 's'
                                          }`}
                                </Button>
                            </div>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>

            <Dialog
                open={blockConfirm !== null}
                onClose={closeBlockConfirm}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: { borderRadius: 12, padding: 8 },
                }}
            >
                {blockConfirm && (
                    <>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            pt={3}
                            pb={1}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    backgroundColor: '#E2563B15',
                                }}
                            >
                                <BlockIcon style={{ color: '#E2563B' }} />
                            </Box>
                        </Box>
                        <DialogTitle
                            disableTypography
                            style={{ textAlign: 'center', paddingBottom: 0 }}
                        >
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 600, fontSize: '1.1rem' }}
                            >
                                Block tenant
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ textAlign: 'center', lineHeight: 1.6 }}
                            >
                                Block <strong>{blockConfirm.tenantName}</strong>{' '}
                                from receiving renewal offers and concessions?
                                This can be undone later.
                            </Typography>
                        </DialogContent>
                        <DialogActions
                            style={{ padding: '8px 24px 20px', gap: 8 }}
                        >
                            <Button
                                onClick={closeBlockConfirm}
                                variant="outlined"
                                fullWidth
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={confirmBlock}
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    color: '#fff',
                                    backgroundColor: '#E2563B',
                                    boxShadow: 'none',
                                }}
                            >
                                Block tenant
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog
                open={unblockConfirm !== null}
                onClose={closeUnblockConfirm}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: { borderRadius: 12, padding: 8 },
                }}
            >
                {unblockConfirm && (
                    <>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            pt={3}
                            pb={1}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    backgroundColor: '#04785715',
                                }}
                            >
                                <CheckCircleOutline
                                    style={{ color: '#047857' }}
                                />
                            </Box>
                        </Box>
                        <DialogTitle
                            disableTypography
                            style={{ textAlign: 'center', paddingBottom: 0 }}
                        >
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 600, fontSize: '1.1rem' }}
                            >
                                Unblock tenant
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ textAlign: 'center', lineHeight: 1.6 }}
                            >
                                Unblock{' '}
                                <strong>{unblockConfirm.tenantName}</strong>?
                                They will appear in the eligible tenants list
                                and can receive renewal offers again.
                            </Typography>
                        </DialogContent>
                        <DialogActions
                            style={{ padding: '8px 24px 20px', gap: 8 }}
                        >
                            <Button
                                onClick={closeUnblockConfirm}
                                variant="outlined"
                                fullWidth
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={confirmUnblock}
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    color: '#fff',
                                    backgroundColor: '#047857',
                                    boxShadow: 'none',
                                }}
                            >
                                Unblock tenant
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    )
}
